import { motion } from 'framer-motion';
import { TrendingUp, DollarSign, ShoppingCart, Home, Zap, AlertTriangle, Target, ArrowRight, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LearnMore = () => {
  const handleGetStarted = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-card-border bg-card/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="px-6 md:px-12 lg:px-24 h-16 flex items-center justify-between">
          <Link to="/" className="font-heading font-black text-xl">
            Real<span className="text-primary">Flation</span>
          </Link>
          <Button 
            onClick={handleGetStarted}
            size="sm"
            className="bg-primary hover:bg-primary/90 text-white rounded-full font-bold"
          >
            Get Started
          </Button>
        </div>
      </div>

      <div className="px-6 md:px-12 lg:px-24 py-12 max-w-5xl mx-auto">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-16"
        >
          <h1 className="font-heading font-black text-4xl md:text-5xl lg:text-6xl mb-6">
            What is Inflation, <span className="text-primary">Really?</span>
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Inflation is the <span className="text-foreground font-bold">gradual increase</span> in the prices of goods and services over time. When inflation happens, each dollar (or HKD, EUR…) buys <span className="text-foreground font-bold">less</span> than it did before — it's like your money quietly loses purchasing power.
          </p>
        </motion.div>

        {/* What is Inflation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="mb-16"
        >
          <Card className="bg-card border-card-border">
            <CardContent className="p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="bg-secondary/20 p-3 rounded-full">
                  <ShoppingCart className="w-6 h-6 text-secondary" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-2xl mb-3">It's Not Just One Item</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    It's a <span className="text-foreground font-bold">general rise</span> across the economy: groceries, fuel, housing, utilities, dining out, streaming services, and more.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Real World Example */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="mb-16"
        >
          <h2 className="font-heading font-bold text-3xl mb-6">Quick Real-World Example</h2>
          <Card className="bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/30">
            <CardContent className="p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <p className="text-muted-foreground mb-2">If inflation averages 3% per year:</p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="bg-primary/20 p-2 rounded">
                        <DollarSign className="w-5 h-5 text-primary" />
                      </div>
                      <p className="text-lg">Something that costs <span className="font-heading font-bold text-2xl text-primary">$100</span> today…</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="bg-destructive/20 p-2 rounded">
                        <TrendingUp className="w-5 h-5 text-destructive" />
                      </div>
                      <p className="text-lg">…will cost about <span className="font-heading font-bold text-2xl text-destructive">$134</span> in 10 years</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="bg-accent/20 border border-accent/30 rounded-lg p-6">
                    <AlertTriangle className="w-8 h-8 text-accent mb-3" />
                    <p className="text-sm text-muted-foreground">
                      Your savings and salary need to grow <span className="text-accent font-bold">faster</span> than inflation just to maintain the same lifestyle.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Why Should You Care */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="mb-16"
        >
          <h2 className="font-heading font-bold text-3xl mb-6">
            Why Should You Care? <span className="text-muted-foreground text-2xl">(Especially in 2026)</span>
          </h2>
          <div className="prose prose-invert max-w-none">
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              Even moderate inflation (2–4%) compounds quickly. High inflation periods (like 2022–2023) showed many people how fast grocery bills, rent, and everyday costs can surge. Tracking inflation helps you:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { icon: <Target className="w-5 h-5" />, text: 'Understand if your salary raises / investments are really beating rising costs' },
                { icon: <ShoppingCart className="w-5 h-5" />, text: 'See which categories hit your wallet hardest (food? energy? housing?)' },
                { icon: <Zap className="w-5 h-5" />, text: 'Make smarter decisions about saving, investing, spending, and negotiating raises' },
                { icon: <Home className="w-5 h-5" />, text: 'Plan long-term goals (house, retirement, travel) more realistically' }
              ].map((item, idx) => (
                <Card key={idx} className="bg-card border-card-border">
                  <CardContent className="p-4 flex items-start gap-3">
                    <div className="bg-primary/20 p-2 rounded flex-shrink-0">
                      {item.icon}
                    </div>
                    <p className="text-sm text-muted-foreground">{item.text}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </motion.div>

        {/* How is Inflation Measured */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.4 }}
          className="mb-16"
        >
          <h2 className="font-heading font-bold text-3xl mb-6">How is Inflation Measured?</h2>
          <Card className="bg-card border-card-border mb-6">
            <CardContent className="p-8">
              <p className="text-muted-foreground mb-6 leading-relaxed">
                Governments use big baskets of thousands of products and services. The most common indicators are:
              </p>
              <div className="space-y-4">
                <div className="border-l-4 border-primary pl-4">
                  <h3 className="font-bold text-lg mb-1">CPI (Consumer Price Index)</h3>
                  <p className="text-sm text-muted-foreground">Everyday consumer goods & services</p>
                </div>
                <div className="border-l-4 border-secondary pl-4">
                  <h3 className="font-bold text-lg mb-1">Core CPI</h3>
                  <p className="text-sm text-muted-foreground">Excludes volatile food & energy prices</p>
                </div>
                <div className="border-l-4 border-accent pl-4">
                  <h3 className="font-bold text-lg mb-1">PCE (Personal Consumption Expenditures)</h3>
                  <p className="text-sm text-muted-foreground">The Federal Reserve's preferred measure</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <p className="text-muted-foreground text-sm">
            Rates are usually reported monthly and as a <span className="text-foreground font-bold">year-over-year</span> percentage (e.g. +2.7% means prices rose 2.7% compared to the same month last year).
          </p>
        </motion.div>

        {/* How RealFlation Helps */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.5 }}
          className="mb-16"
        >
          <h2 className="font-heading font-bold text-3xl mb-6">
            How <span className="text-primary">RealFlation</span> Helps You Take Control
          </h2>
          <Card className="bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/30 mb-6">
            <CardContent className="p-8">
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                Unlike official statistics that show national averages, our app lets you:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  'Track your personal inflation → based on the things you actually buy',
                  'Visualize trends over 1 month / 6 months / 1–5 years',
                  'Compare your spending categories against official CPI data',
                  'See real-time alerts when inflation accelerates in categories important to you',
                  'Calculate how much extra income you\'d need to maintain your current lifestyle',
                  'Regional data for USA states, EU countries, and Hong Kong'
                ].map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="bg-success/20 p-1 rounded-full mt-1 flex-shrink-0">
                      <div className="w-2 h-2 bg-success rounded-full"></div>
                    </div>
                    <p className="text-sm text-muted-foreground">{feature}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Whether you're budgeting more tightly, protecting your savings, or planning investments, understanding <span className="text-foreground font-bold">your</span> inflation rate is one of the most powerful things you can do for your financial health.
          </p>
        </motion.div>

        {/* Quick Tips */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.6 }}
          className="mb-16"
        >
          <h2 className="font-heading font-bold text-3xl mb-6">Quick Tips to Fight Inflation</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { icon: <TrendingUp className="w-5 h-5" />, tip: 'Invest in assets that tend to outpace inflation (stocks, real estate, inflation-linked bonds)' },
              { icon: <Target className="w-5 h-5" />, tip: 'Review your budget regularly — small leaks become big problems when prices rise' },
              { icon: <DollarSign className="w-5 h-5" />, tip: 'Negotiate salary / freelance rates at least as often as inflation rises' },
              { icon: <ShoppingCart className="w-5 h-5" />, tip: 'Buy in bulk or during promotions for frequently used items' }
            ].map((item, idx) => (
              <Card key={idx} className="bg-card border-card-border hover:border-primary/50 transition-colors">
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="bg-primary/20 p-3 rounded-full flex-shrink-0">
                    {item.icon}
                  </div>
                  <p className="text-sm text-muted-foreground">{item.tip}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.7 }}
          className="mb-16"
        >
          <Card className="bg-gradient-to-br from-primary/20 to-secondary/20 border-primary/50">
            <CardContent className="p-12 text-center">
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-4">
                Ready to see how inflation is <span className="text-primary">really</span> affecting your life?
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Start tracking your own basket now!
              </p>
              <Button 
                onClick={handleGetStarted}
                size="lg" 
                className="bg-primary hover:bg-primary/90 text-white rounded-full font-bold px-8 transition-transform hover:scale-105 active:scale-95"
              >
                Start Tracking Now <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </CardContent>
          </Card>
        </motion.div>

        {/* Contact Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.9 }}
          className="mb-16"
        >
          <Card className="bg-card border-card-border">
            <CardContent className="p-8 text-center">
              <h3 className="font-heading font-bold text-2xl mb-4">Get in Touch</h3>
              <p className="text-muted-foreground mb-6">
                Have questions or feedback? We'd love to hear from you.
              </p>
              
              <div className="space-y-4">
                {/* Email Contact */}
                <div className="flex items-center justify-center gap-2 text-lg">
                  <span className="text-muted-foreground">Contact:</span>
                  <a 
                    href="mailto:info@realflation.net" 
                    className="text-primary hover:text-primary/80 font-medium transition-colors"
                  >
                    info@realflation.net
                  </a>
                </div>
                
                {/* Social Media */}
                <div className="flex items-center justify-center gap-6 pt-4">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Follow us:</span>
                  </div>
                  <a 
                    href="https://instagram.com/realflation" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:text-primary/80 font-medium transition-colors flex items-center gap-1"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                    @realflation
                  </a>
                  <a 
                    href="https://x.com/realflation" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:text-primary/80 font-medium transition-colors flex items-center gap-1"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    @realflation
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.8 }}
          className="mb-16"
        >
          <div className="flex items-center gap-3 mb-8">
            <div className="bg-primary/20 p-3 rounded-full">
              <HelpCircle className="w-6 h-6 text-primary" />
            </div>
            <h2 className="font-heading font-bold text-3xl">Frequently Asked Questions</h2>
          </div>
          
          <Card className="bg-card border-card-border">
            <CardContent className="p-6">
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="item-1" className="border-card-border">
                  <AccordionTrigger className="text-left hover:text-primary transition-colors">
                    Do I need to link my credit cards or bank accounts?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    No — RealFlation is designed with maximum privacy in mind. You can manually enter expenses or use our Plaid integration (optional). We never ask for — and never store — your bank login credentials, credit card numbers, CVV, or full account details. Your personal inflation tracking stays 100% under your control.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-2" className="border-card-border">
                  <AccordionTrigger className="text-left hover:text-primary transition-colors">
                    How does RealFlation keep my data private and secure?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    We use industry-standard 256-bit AES encryption for all data stored on our servers and in transit (HTTPS/TLS 1.3+). Your spending entries and custom baskets are encrypted end-to-end. We do not sell, share, or monetize your personal financial data with third parties. We comply with GDPR, CCPA, and relevant Hong Kong data protection regulations.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-3" className="border-card-border">
                  <AccordionTrigger className="text-left hover:text-primary transition-colors">
                    What personal information does the app collect?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    Only the minimum needed: your email (for account access via Google Auth), optional region selection (for local inflation benchmarks, can be changed anytime), and the spending categories/prices you choose to input. We do not collect full credit card numbers, bank details, precise geolocation without consent, or device identifiers for tracking purposes.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-4" className="border-card-border">
                  <AccordionTrigger className="text-left hover:text-primary transition-colors">
                    Can I use RealFlation offline?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    Core tracking and calculations work in your browser. An internet connection is required to sync data across devices, fetch official inflation rates, and access your account. However, once loaded, you can view your existing data and continue tracking expenses even with intermittent connectivity.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-5" className="border-card-border">
                  <AccordionTrigger className="text-left hover:text-primary transition-colors">
                    What happens if I delete my account?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    When you delete your account (available in settings), all your personal spending data, expense history, and calculations are permanently deleted from our servers within 30 days. We retain only anonymized, aggregated statistics to improve general inflation insights — nothing traceable to you.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-6" className="border-card-border">
                  <AccordionTrigger className="text-left hover:text-primary transition-colors">
                    Is my payment information stored when I use premium features?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    We never store your full payment details on our servers. All payments are processed securely through trusted providers (e.g., Stripe), which are PCI DSS Level 1 compliant. They handle tokenization — we only receive a secure token, never your actual card number or CVV.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-7" className="border-card-border">
                  <AccordionTrigger className="text-left hover:text-primary transition-colors">
                    How do you compare my personal inflation to official CPI?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    We pull publicly available, anonymized official statistics (e.g., Hong Kong CPI from Census and Statistics Department, US CPI from BLS, Eurostat for EU) — no personal data is sent to external services. Your custom basket stays private; we only use it to calculate your personal rate and show side-by-side comparisons on your dashboard.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-8" className="border-card-border">
                  <AccordionTrigger className="text-left hover:text-primary transition-colors">
                    What if I accidentally enter sensitive info in my expenses?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    We strongly advise against entering any sensitive financial details (card numbers, passwords, etc.) in notes or descriptions. Your entries are private and encrypted. You can edit or delete them anytime. For maximum safety, use only category names, item descriptions, amounts, and dates.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-9" className="border-card-border">
                  <AccordionTrigger className="text-left hover:text-primary transition-colors">
                    Do you share my data with third parties or use it for AI?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    No. We do not share identifiable user data with third parties, advertisers, or data brokers. We do not use your personal spending entries to train AI models. Any future improvements use only anonymized, aggregated trends (e.g., "Food category rose X% on average across all users").
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-10" className="border-b-0">
                  <AccordionTrigger className="text-left hover:text-primary transition-colors">
                    How can I verify that my data is safe?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    We follow security best practices including encryption, regular security reviews, and compliance with international standards. Our privacy policy is transparent and regularly updated (last update: January 2026). If you have concerns or questions, our support team is available to help clarify our security measures.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Footer */}
      <div className="border-t border-card-border mt-24 py-8">
        <div className="px-6 md:px-12 lg:px-24 text-center text-sm text-muted-foreground">
          <p>© 2026 RealFlation. Track your real inflation.</p>
        </div>
      </div>
    </div>
  );
};

export default LearnMore;
